export const VISITOR_ACTIVITY_STORAGE_KEY =
	"highrays.visitor-activity-purchases.v3";
export const INITIAL_VISITOR_ACTIVITY_BATCH_SIZE = 50;
export const REFILL_VISITOR_ACTIVITY_BATCH_SIZE = 100;
export const DEFAULT_VISITOR_ACTIVITY_TIMING_RANGES = {
	initialDelayMs: [8_000, 16_000],
	intervalMs: [22_000, 52_000],
	visibleDurationMs: [5_600, 8_200],
} as const;

const STORE_VERSION = 3;
const OBFUSCATION_KEY = "thehighrays-visitor-purchase-popups-v3";
const DEFAULT_CHANNEL_NAME_COUNT = 2000;

const CHANNEL_LEADS = [
	"Daily",
	"Pixel",
	"Urban",
	"Bright",
	"Hype",
	"Neon",
	"Hidden",
	"Prime",
	"Epic",
	"Fresh",
	"Viral",
	"Golden",
	"Tiny",
	"Mega",
	"Next",
	"True",
	"Cosmic",
	"Retro",
	"Modern",
	"Bold",
	"Wild",
	"Happy",
	"Indian",
	"Global",
	"Street",
	"Craft",
	"LevelUp",
	"Rapid",
	"Clear",
	"Royal",
	"Dream",
	"Smart",
	"Laugh",
	"Focus",
	"Peak",
	"Trend",
	"Chill",
	"Spark",
	"Mighty",
	"Open",
	"Local",
	"Nomad",
	"Alpha",
	"Creator",
	"Studio",
	"Shorts",
	"Reel",
	"Frame",
	"Lens",
	"Wave",
] as const;

const CHANNEL_TOPICS = [
	"Creator",
	"Gaming",
	"Food",
	"Travel",
	"Shorts",
	"Reels",
	"Cinema",
	"Sports",
	"Fitness",
	"Tech",
	"Finance",
	"Beauty",
	"Music",
	"Comedy",
	"Podcast",
	"Study",
	"Art",
	"Auto",
	"News",
	"Family",
	"Lifestyle",
	"Motivation",
	"Camera",
	"Review",
	"Story",
	"Reaction",
	"Kitchen",
	"Market",
	"Design",
	"Learning",
] as const;

const CHANNEL_FORMATS = [
	"Vlogs",
	"TV",
	"Studio",
	"Channel",
	"Clips",
	"Diaries",
	"Show",
	"Hub",
	"Cuts",
	"Stories",
	"Lab",
	"Live",
	"Network",
	"Daily",
	"Project",
	"House",
	"Room",
	"World",
	"Desk",
	"Squad",
	"Spot",
	"Vault",
	"Stream",
	"Corner",
	"Diary",
] as const;

const VISITOR_ACTIVITY_PLAN_AMOUNTS = [
	{ inr: "Rs. 1,499", usd: "$19" },
	{ inr: "Rs. 2,499", usd: "$29" },
	{ inr: "Rs. 4,999", usd: "$49" },
	{ inr: "Rs. 7,499", usd: "$79" },
	{ inr: "Rs. 9,999", usd: "$99" },
	{ inr: "Rs. 14,999", usd: "$149" },
	{ inr: "Rs. 19,999", usd: "$199" },
	{ inr: "Rs. 24,999", usd: "$249" },
	{ inr: "Rs. 39,999", usd: "$399" },
	{ inr: "Rs. 49,999", usd: "$499" },
	{ inr: "Rs. 74,999", usd: "$749" },
	{ inr: "Rs. 99,999", usd: "$999" },
] as const;

export type VisitorActivityCurrency = "INR" | "USD";

export interface VisitorActivityPurchase {
	channelName: string;
	planName: string;
}

export interface VisitorActivityStore {
	version: typeof STORE_VERSION;
	cursor: number;
	seed: number;
	currency: VisitorActivityCurrency;
	channelNames: string[];
	planNames: string[];
}

export type VisitorActivityTimingRange = readonly [number, number];

interface CreateVisitorActivityStoreOptions {
	channelNames?: string[];
	planNames?: string[];
	seed?: number;
	currency?: VisitorActivityCurrency;
}

let cachedChannelNames: string[] | null = null;

function hashStringToUint32(value: string): number {
	let hash = 2_166_136_261;

	for (let index = 0; index < value.length; index += 1) {
		hash ^= value.charCodeAt(index);
		hash = Math.imul(hash, 16_777_619);
	}

	return hash >>> 0;
}

function gcd(firstValue: number, secondValue: number): number {
	let first = Math.abs(firstValue);
	let second = Math.abs(secondValue);

	while (second !== 0) {
		const remainder = first % second;
		first = second;
		second = remainder;
	}

	return first;
}

function createVisitorActivitySeed(): number {
	const cryptoObject = globalThis.crypto;
	if (cryptoObject?.getRandomValues) {
		const values = new Uint32Array(1);
		cryptoObject.getRandomValues(values);
		return values[0] || 1;
	}

	return (Date.now() ^ Math.floor(Math.random() * 0xffffffff)) >>> 0 || 1;
}

function normalizeRange(range: VisitorActivityTimingRange): [number, number] {
	const min = Math.max(0, Math.floor(range[0]));
	const max = Math.max(min, Math.floor(range[1]));
	return [min, max];
}

export function getSeededRangeValue(
	seed: number,
	index: number,
	salt: string,
	range: VisitorActivityTimingRange,
): number {
	const [min, max] = normalizeRange(range);
	if (min === max) return min;

	const hash = hashStringToUint32(`${seed}:${index}:${salt}`);
	return min + (hash % (max - min + 1));
}

function isVisitorActivityCurrency(
	value: unknown,
): value is VisitorActivityCurrency {
	return value === "INR" || value === "USD";
}

export function getVisitorActivityCurrencyFromPricingCountry(
	country: string | null | undefined,
): VisitorActivityCurrency | null {
	const normalized = country?.trim().toLowerCase();
	if (!normalized) return null;
	if (normalized === "in" || normalized === "inr") return "INR";
	if (normalized === "usd") return "USD";
	return "USD";
}

export function getVisitorActivityCurrency({
	country,
	languages = [],
	timeZone,
}: {
	country?: string | null;
	languages?: readonly string[];
	timeZone?: string | null;
} = {}): VisitorActivityCurrency {
	const trustedCountryCurrency =
		getVisitorActivityCurrencyFromPricingCountry(country);
	if (trustedCountryCurrency) return trustedCountryCurrency;

	const hasIndianLanguage = languages.some((language) =>
		/(^|-|_)in$/i.test(language),
	);

	if (
		hasIndianLanguage ||
		timeZone === "Asia/Kolkata" ||
		timeZone === "Asia/Calcutta"
	) {
		return "INR";
	}

	return "USD";
}

export function getVisitorActivityPlanNames(
	currency: VisitorActivityCurrency = "USD",
): string[] {
	return VISITOR_ACTIVITY_PLAN_AMOUNTS.map(
		(amount) =>
			`Targeted Views for ${currency === "INR" ? amount.inr : amount.usd}`,
	);
}

export function getVisitorActivityChannelNames(
	count = DEFAULT_CHANNEL_NAME_COUNT,
): string[] {
	if (cachedChannelNames && cachedChannelNames.length >= count) {
		return cachedChannelNames.slice(0, count);
	}

	const candidates: string[] = [];

	for (const lead of CHANNEL_LEADS) {
		for (const topic of CHANNEL_TOPICS) {
			for (const format of CHANNEL_FORMATS) {
				candidates.push(`${lead}${topic} ${format}`);
			}
		}
	}

	cachedChannelNames = [...new Set(candidates)]
		.sort(
			(first, second) => hashStringToUint32(first) - hashStringToUint32(second),
		)
		.slice(0, DEFAULT_CHANNEL_NAME_COUNT);

	return cachedChannelNames.slice(0, count);
}

export function createVisitorActivityStore(
	options: CreateVisitorActivityStoreOptions = {},
): VisitorActivityStore {
	const currency = options.currency ?? "USD";

	return {
		version: STORE_VERSION,
		cursor: 0,
		seed: options.seed ?? createVisitorActivitySeed(),
		currency,
		channelNames: [
			...(options.channelNames ?? getVisitorActivityChannelNames()),
		],
		planNames: [
			...(options.planNames ?? getVisitorActivityPlanNames(currency)),
		],
	};
}

function xorText(value: string): string {
	let output = "";

	for (let index = 0; index < value.length; index += 1) {
		const keyCode = OBFUSCATION_KEY.charCodeAt(index % OBFUSCATION_KEY.length);
		output += String.fromCharCode(value.charCodeAt(index) ^ keyCode);
	}

	return output;
}

export function encodeVisitorActivityStore(
	store: VisitorActivityStore,
): string {
	return window.btoa(xorText(JSON.stringify(store)));
}

function isNonEmptyStringArray(value: unknown): value is string[] {
	return (
		Array.isArray(value) &&
		value.length > 0 &&
		value.every((item) => typeof item === "string" && item.trim().length > 0)
	);
}

function isVisitorActivityStore(value: unknown): value is VisitorActivityStore {
	if (!value || typeof value !== "object") return false;

	const candidate = value as Partial<VisitorActivityStore>;

	return (
		candidate.version === STORE_VERSION &&
		Number.isInteger(candidate.cursor) &&
		(candidate.cursor ?? -1) >= 0 &&
		Number.isInteger(candidate.seed) &&
		(candidate.seed ?? 0) > 0 &&
		isVisitorActivityCurrency(candidate.currency) &&
		isNonEmptyStringArray(candidate.channelNames) &&
		isNonEmptyStringArray(candidate.planNames)
	);
}

export function decodeVisitorActivityStore(
	encoded: string | null,
): VisitorActivityStore | null {
	if (!encoded) return null;

	try {
		const payload = JSON.parse(xorText(window.atob(encoded))) as unknown;
		if (!isVisitorActivityStore(payload)) return null;

		const totalCombinations =
			payload.channelNames.length * payload.planNames.length;

		return {
			version: STORE_VERSION,
			cursor: Math.min(payload.cursor, totalCombinations),
			seed: payload.seed,
			currency: payload.currency,
			channelNames: payload.channelNames,
			planNames: payload.planNames,
		};
	} catch {
		return null;
	}
}

function getPermutationParams(seed: number, totalCombinations: number) {
	if (totalCombinations <= 1) {
		return { offset: 0, step: 1 };
	}

	const offset =
		hashStringToUint32(`${seed}:visitor-activity-offset`) % totalCombinations;
	let step =
		(hashStringToUint32(`${seed}:visitor-activity-step`) %
			(totalCombinations - 1)) +
		1;

	while (gcd(step, totalCombinations) !== 1) {
		step = (step % (totalCombinations - 1)) + 1;
	}

	return { offset, step };
}

function getPurchaseAtCursor(
	store: VisitorActivityStore,
	cursor: number,
): VisitorActivityPurchase {
	const totalCombinations = store.channelNames.length * store.planNames.length;
	const { offset, step } = getPermutationParams(store.seed, totalCombinations);
	const purchaseIndex = (offset + cursor * step) % totalCombinations;
	const channelIndex = purchaseIndex % store.channelNames.length;
	const planIndex =
		Math.floor(purchaseIndex / store.channelNames.length) %
		store.planNames.length;

	return {
		channelName: store.channelNames[channelIndex],
		planName: store.planNames[planIndex],
	};
}

export function reserveVisitorActivityBatch(
	store: VisitorActivityStore,
	batchSize: number,
): { store: VisitorActivityStore; batch: VisitorActivityPurchase[] } {
	const safeBatchSize = Math.max(0, Math.floor(batchSize));
	const totalCombinations = store.channelNames.length * store.planNames.length;
	const cursor = Math.min(store.cursor, totalCombinations);
	const nextCursor = Math.min(cursor + safeBatchSize, totalCombinations);
	const batch: VisitorActivityPurchase[] = [];

	for (let index = cursor; index < nextCursor; index += 1) {
		batch.push(getPurchaseAtCursor(store, index));
	}

	return {
		store: {
			...store,
			cursor: nextCursor,
		},
		batch,
	};
}

export function loadVisitorActivityStore(
	storage: Storage,
	seed?: number,
	options: CreateVisitorActivityStoreOptions = {},
): VisitorActivityStore {
	const decoded = decodeVisitorActivityStore(
		storage.getItem(VISITOR_ACTIVITY_STORAGE_KEY),
	);
	const currency = options.currency ?? "USD";

	if (
		decoded &&
		(seed === undefined || decoded.seed === seed) &&
		decoded.currency === currency
	) {
		return decoded;
	}

	const store = createVisitorActivityStore({ ...options, seed, currency });
	storage.setItem(
		VISITOR_ACTIVITY_STORAGE_KEY,
		encodeVisitorActivityStore(store),
	);
	return store;
}

export function persistVisitorActivityStore(
	storage: Storage,
	store: VisitorActivityStore,
) {
	storage.setItem(
		VISITOR_ACTIVITY_STORAGE_KEY,
		encodeVisitorActivityStore(store),
	);
}
